import React, { useState, useEffect, useRef } from 'react';
import Filters from './components/Filters';
import TankList from './components/TankList';
import FilterStatusBar from './components/FilterStatusBar';
import TankDetailModal from './components/TankDetailModal';
import AddToCartConfirmationModal from './components/AddToCartConfirmationModal';

const TANK_DATA_URL = 'https://elkhart-rv-parts.s3.us-east-1.amazonaws.com/generated-data/water-tanks.json';

// Placeholder Variant IDs - REMOVED
// const UPSELL_SEALANT_VARIANT_ID = 'YOUR_SEALANT_VARIANT_ID';
// const UPSELL_HOLDING_FITTINGS_VARIANT_ID = 'YOUR_HOLDING_FITTINGS_VARIANT_ID';
// const UPSELL_FRESH_FITTINGS_VARIANT_ID = 'YOUR_FRESH_FITTINGS_VARIANT_ID';

// Helper function to set cart attributes for tracking
const setCartAttributes = async () => {
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          'Tank_Finder_App': 'Used',
          'Tank_Finder_Timestamp': new Date().toISOString()
        }
      })
    });
    
    if (!response.ok) {
      console.error('Failed to set cart attributes:', response.status);
      return;
    }
    
    const data = await response.json();
    console.log('Cart attributes updated:', data);
  } catch (error) {
    console.error('Error setting cart attributes:', error);
  }
};

// Helper function to get min/max values for a dimension from tanks (moved from Filters.js)
function getDimensionBounds(tanks, dimension) {
  if (!tanks || tanks.length === 0) return { min: 0, max: 0 };
  const values = tanks.map(tank => Number(tank[dimension])).filter(v => !isNaN(v));
  if (values.length === 0) return { min: 0, max: 0 };
  return { min: Math.min(...values), max: Math.max(...values) };
}

const initialDimensions = { gallons: {min:0, max:0}, length: {min:0, max:0}, width: {min:0, max:0}, height: {min:0, max:0} };

// Sorting function (moved from TankList.js)
const sortTanks = (tanksToSort) => {
  return [...tanksToSort].sort((a, b) => {
    const aHasInventory = a.inventoryCount > 0;
    const bHasInventory = b.inventoryCount > 0;
    if (aHasInventory && !bHasInventory) return -1;
    if (!aHasInventory && bHasInventory) return 1;
    return a.gallons - b.gallons;
  });
};

function App() {
  // Initial state setup - will be potentially overridden by URL params on load
  const [selectedType, setSelectedType] = useState('fresh');
  const [allTanks, setAllTanks] = useState([]);
  const [filteredTanks, setFilteredTanks] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start true until URL/initial data loaded
  const [error, setError] = useState(null);
  const [dimensionBounds, setDimensionBounds] = useState(initialDimensions);
  const [currentFilterRanges, setCurrentFilterRanges] = useState(initialDimensions);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState('list');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTankForModal, setSelectedTankForModal] = useState(null);
  const [currentIndexInFilteredTanks, setCurrentIndexInFilteredTanks] = useState(0);
  const [isInitialURLLoaded, setIsInitialURLLoaded] = useState(false); // Renamed for clarity
  const [isCartConfirmationModalOpen, setIsCartConfirmationModalOpen] = useState(false); // State for the new modal
  const [cartDetails, setCartDetails] = useState({ itemCount: 0, totalPrice: 0 }); // State for cart details

  const prevSelectedTypeForFilterResetRef = useRef(selectedType);
  const initialUrlProcessingAttemptedRef = useRef(false);

  // Removed Upsell Modal and Cart state
  // const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  // const [lastAddedTank, setLastAddedTank] = useState(null);
  // const [cartError, setCartError] = useState(null);
  // const SHOPIFY_CART_URL = '/cart'; 
  // const SHOPIFY_CHECKOUT_URL = '/checkout'; 

  // Set cart attribute when app loads
  useEffect(() => {
    setCartAttributes();
  }, []);

  const handleAddToCart = async (variantId) => {
    if (!variantId) {
      console.error('Add to cart error: Variant ID is missing.');
      alert('Could not add item to cart. Product identifier is missing.');
      return;
    }
    try {
      const formData = new FormData();
      // IMPORTANT: Replace 'id' if your Shopify setup expects a different parameter name for variant ID.
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        // FormData sets Content-Type to 'multipart/form-data'
        // Shopify's /cart/add.js endpoint also accepts 'application/x-www-form-urlencoded' or 'application/json'
        // If issues arise, you might need to explicitly set Content-Type and format the body accordingly.
      });

      if (!response.ok) {
        // Attempt to parse error response from Shopify
        let errorData = { message: `Request failed with status ${response.status}` };
        try {
          errorData = await response.json();
        } catch (e) {
          // If response is not JSON, use status text
          errorData.message = response.statusText || errorData.message;
        }
        console.error('Failed to add to cart:', response.status, errorData);
        alert(`Error adding to cart: ${errorData.description || errorData.message || 'Unknown error'}`);
        return;
      }

      const cartData = await response.json();
      console.log('Successfully added to cart:', cartData);
      setCartDetails({ 
        itemCount: cartData.item_count || 0, 
        totalPrice: cartData.total_price || 0 
      });
      setIsCartConfirmationModalOpen(true); // Open the confirmation modal
      // Optionally, you can redirect to the cart page or update a cart icon/count here
      // For example: window.location.href = '/cart';
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('An unexpected error occurred while adding the item to cart.');
    }
  };

  // Effect to:
  // 1. Read URL on Initial Load (first render only, or if type changes via URL before initial processing)
  // 2. Fetch Data whenever selectedType changes
  // 3. Set initial/reset dimensionBounds and currentFilterRanges after data fetch
  useEffect(() => {
    let typeForThisRun = selectedType;
    let filtersFromURL = null;
    
    // Determine if we need to parse URL parameters for initialization.
    // This should only happen once successfully.
    let needsUrlProcessing = !isInitialURLLoaded && !initialUrlProcessingAttemptedRef.current;

    if (needsUrlProcessing) {
      initialUrlProcessingAttemptedRef.current = true; // Mark that we are attempting URL processing in this effect cycle.
      const params = new URLSearchParams(window.location.search);
      const typeFromURL = params.get('type') || 'fresh';
      const displayModeFromURL = params.get('display') || 'list';

      let urlFilterRanges = {};
      let hasUrlFilters = false;
      ['gallons', 'length', 'width', 'height'].forEach(dim => {
        const min = params.get(`${dim}_min`);
        const max = params.get(`${dim}_max`);
        if (min !== null && max !== null) {
          urlFilterRanges[dim] = { min: parseFloat(min), max: parseFloat(max) };
          hasUrlFilters = true;
        }
      });
      if (hasUrlFilters) {
        filtersFromURL = urlFilterRanges;
      }

      // If URL specifies a type different from current state, update state and bail.
      // The effect will re-run with the new selectedType, and needsUrlProcessing will be re-evaluated.
      if (selectedType !== typeFromURL) {
        setSelectedType(typeFromURL);
        // displayMode will be set in the re-run when selectedType matches typeFromURL
        return;
      }
      // If types match, proceed with this type and set displayMode.
      typeForThisRun = typeFromURL; // or selectedType, they are the same here
      setDisplayMode(displayModeFromURL);
    }

    const fetchDataAndSetFilters = async () => {
      setIsLoading(true); setError(null);
      try {
        const response = await fetch(TANK_DATA_URL);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const relevantTanks = data.filter(tank => {
          if (typeForThisRun === 'fresh' && tank.productType === 'Fresh Water Tanks') return true;
          if (typeForThisRun === 'holding' && (tank.productType === 'Holding Tanks')) return true;
          return false;
        });
        const processedTanks = relevantTanks.map(tank => ({...tank, gallons: parseFloat(tank.gallons) || 0, length: parseFloat(tank.length) || 0, width: parseFloat(tank.width) || 0, height: parseFloat(tank.height) || 0, price: parseFloat(tank.price) || 0, inventoryCount: parseInt(tank.inventoryCount, 10) || 0,}));
        setAllTanks(processedTanks);

        const newBounds = {
          gallons: getDimensionBounds(processedTanks, 'gallons'),
          length: getDimensionBounds(processedTanks, 'length'),
          width: getDimensionBounds(processedTanks, 'width'),
          height: getDimensionBounds(processedTanks, 'height'),
        };
        setDimensionBounds(newBounds);

        // Always set the filter ranges to the full bounds initially
        // unless there are specific URL filters present
        let initialRangesToSet = { ...newBounds };

        if (needsUrlProcessing && filtersFromURL) { // Apply URL filters only on the designated initial pass
          Object.keys(filtersFromURL).forEach(dim => {
            if (newBounds[dim] && filtersFromURL[dim]) {
              let validatedMin = Math.max(newBounds[dim].min, Math.min(filtersFromURL[dim].min, newBounds[dim].max));
              let validatedMax = Math.min(newBounds[dim].max, Math.max(filtersFromURL[dim].max, newBounds[dim].min));
              if (validatedMin > validatedMax) validatedMin = validatedMax;
              initialRangesToSet[dim] = { min: validatedMin, max: validatedMax };
            }
          });
        } else if (prevSelectedTypeForFilterResetRef.current !== selectedType) {
          // Not using URL params, or changing tank type - use full bounds
          initialRangesToSet = { ...newBounds };
        }
        
        // Always set filter ranges to something valid
        setCurrentFilterRanges(initialRangesToSet);
        
        if (needsUrlProcessing) {
            setIsInitialURLLoaded(true); // Mark that all initial URL-driven setup is complete.
        }
        setIsFilterPanelOpen(false);
      } catch (e) {
        console.error("Failed to fetch tank data:", e); setError('Failed to load tank data. Please try again later.'); setAllTanks([]);
        if (needsUrlProcessing) { // If initial load fails, still mark it as "loaded" to prevent loops/stuck state
            setIsInitialURLLoaded(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchDataAndSetFilters();

    // Update the ref for the next comparison after the effect's logic has run.
    prevSelectedTypeForFilterResetRef.current = selectedType;

  }, [selectedType, isInitialURLLoaded]); // isInitialURLLoaded is now a dependency


  // Effect to filter AND SORT tanks (runs after currentFilterRanges or allTanks might have changed)
  useEffect(() => {
    if (!allTanks || allTanks.length === 0) {
      setFilteredTanks([]); 
      return;
    }

    const allDimensionsPresent = ['gallons', 'length', 'width', 'height'].every(
      dim => currentFilterRanges[dim] && typeof currentFilterRanges[dim].min === 'number' && typeof currentFilterRanges[dim].max === 'number'
    );

    if (!allDimensionsPresent) {
      console.warn("Not all dimensions have valid min/max values, skipping filtering");
      setFilteredTanks(sortTanks(allTanks)); // Show all tanks sorted if filters are invalid
      return; 
    }

    // Check if all filter ranges are effectively zero (initial state)
    const allFiltersZero = ['gallons', 'length', 'width', 'height'].every(
      dim => currentFilterRanges[dim].min === 0 && currentFilterRanges[dim].max === 0
    );

    if (allFiltersZero && dimensionBounds.gallons.max > 0) {
      // If filters are all zeros but we have actual tanks with non-zero dimensions,
      // this likely means we haven't properly initialized filters yet
      console.log("Filters appear to be zeros but tanks have dimensions - showing all tanks");
      setFilteredTanks(sortTanks(allTanks));
      return;
    }

    const applyFiltersAndSort = () => {
      let newFilteredTanks = allTanks.filter(tank => (
          tank.gallons >= currentFilterRanges.gallons.min && tank.gallons <= currentFilterRanges.gallons.max &&
          tank.length >= currentFilterRanges.length.min && tank.length <= currentFilterRanges.length.max &&
          tank.width >= currentFilterRanges.width.min && tank.width <= currentFilterRanges.width.max &&
          tank.height >= currentFilterRanges.height.min && tank.height <= currentFilterRanges.height.max
      ));
      newFilteredTanks = sortTanks(newFilteredTanks);
      setFilteredTanks(newFilteredTanks);
    };
    applyFiltersAndSort(); 
  }, [currentFilterRanges, allTanks, dimensionBounds.gallons.max]);

  const handleTypeSelect = (type) => setSelectedType(type);

  // Callback for when filters change in the Filters component
  const handleFilterRangesChange = (newRanges) => setCurrentFilterRanges(newRanges);
  
  // To open the main filter panel from status bar
  const openFilterPanel = () => setIsFilterPanelOpen(true);

  // To close the main filter panel from Filters.js's button
  const closeFilterPanelAndApply = () => setIsFilterPanelOpen(false);

  const handleOpenModal = (tankIndex) => {
    if (filteredTanks && filteredTanks[tankIndex]) {
      setSelectedTankForModal(filteredTanks[tankIndex]);
      setCurrentIndexInFilteredTanks(tankIndex);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTankForModal(null);
  };

  const handleShowNextTankInModal = () => {
    const nextIndex = currentIndexInFilteredTanks + 1;
    if (nextIndex < filteredTanks.length) {
      setSelectedTankForModal(filteredTanks[nextIndex]);
      setCurrentIndexInFilteredTanks(nextIndex);
    }
  };

  const handleShowPreviousTankInModal = () => {
    const prevIndex = currentIndexInFilteredTanks - 1;
    if (prevIndex >= 0) {
      setSelectedTankForModal(filteredTanks[prevIndex]);
      setCurrentIndexInFilteredTanks(prevIndex);
    }
  };

  const handleCloseCartConfirmationModal = () => {
    setIsCartConfirmationModalOpen(false);
  };

  const handleCheckout = () => {
    window.location.href = '/checkout'; // Shopify checkout URL
  };

  const handleViewCart = () => {
    window.location.href = '/cart'; // Shopify cart URL
  };

  // Update URL with current app state
  useEffect(() => {
    // Only update URL when initial loading is complete
    if (!isLoading && isInitialURLLoaded) {
      const params = new URLSearchParams();
      
      // Add core state parameters
      params.set('type', selectedType);
      params.set('display', displayMode);
      
      // Add filter parameters if they exist and differ from the default bounds
      if (currentFilterRanges && dimensionBounds) {
        ['gallons', 'length', 'width', 'height'].forEach(dim => {
          // Only include filters that are different from the full bounds
          if (
            currentFilterRanges[dim] && 
            dimensionBounds[dim] && 
            (currentFilterRanges[dim].min > dimensionBounds[dim].min || 
             currentFilterRanges[dim].max < dimensionBounds[dim].max)
          ) {
            params.set(`${dim}_min`, currentFilterRanges[dim].min);
            params.set(`${dim}_max`, currentFilterRanges[dim].max);
          }
        });
      }
      
      // Update URL without reloading the page
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, [selectedType, displayMode, currentFilterRanges, dimensionBounds, isLoading, isInitialURLLoaded]);

  const tabBaseStyle = "tw-py-3 tw-px-6 tw-font-medium tw-text-lg focus:tw-outline-none tw-transition-colors tw-duration-150";
  const activeTabStyle = "tw-bg-gray-100 tw-text-brand-red tw-border-b-2 tw-border-brand-red";
  const inactiveTabStyle = "tw-text-gray-500 hover:tw-text-gray-700 tw-border-b-2 tw-border-transparent hover:tw-border-gray-300";

  const viewModeButtonBase = "tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tracking-wider";
  const viewModeButtonActive = "tw-text-gray-900";
  const viewModeButtonInactive = "tw-text-gray-400 hover:tw-text-gray-600";

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <header className="tw-bg-white tw-shadow-sm tw-sticky tw-top-0 tw-z-4">
        <div className="tw-max-w-4xl tw-mx-auto tw-px-4">
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-py-4">RV Tank Finder</h1>
          <div className="tw-flex tw-border-b tw-border-gray-200">
            <button onClick={() => handleTypeSelect('fresh')} className={`${tabBaseStyle} ${selectedType === 'fresh' ? activeTabStyle : inactiveTabStyle}`}>Fresh Water Tanks</button>
            <button onClick={() => handleTypeSelect('holding')} className={`${tabBaseStyle} ${selectedType === 'holding' ? activeTabStyle : inactiveTabStyle}`}>Holding Tanks</button>
          </div>
        </div>
      </header>

      <main className="tw-max-w-4xl tw-mx-auto tw-p-0">
        {isLoading ? (
          <p className="tw-text-gray-600 tw-text-center tw-py-10">Loading tanks...</p>
        ) : error ? (
          <p className="tw-text-red-600 tw-text-center tw-py-10">{error}</p>
        ) : (
          <div>
            {allTanks.length > 0 && (
                <FilterStatusBar 
                    dimensionBounds={dimensionBounds}
                    currentRanges={currentFilterRanges}
                    onOpenFilterPanel={openFilterPanel}
                />
            )}
            
            {isFilterPanelOpen && (
              <Filters
                dimensionBounds={dimensionBounds}
                currentRanges={currentFilterRanges}
                onCurrentRangesChange={handleFilterRangesChange}
                onClosePanel={closeFilterPanelAndApply}
                matchingTankCount={filteredTanks.length}
              />
            )}

            {!isFilterPanelOpen && allTanks.length > 0 && (
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-px-1 sm:tw-px-0">
                <h2 className="tw-text-lg tw-font-normal tw-text-gray-600 tw-ml-3">
                  {filteredTanks.length} matching tanks
                </h2>
                <div className="tw-flex tw-space-x-1 sm:tw-space-x-2">
                  <button onClick={() => setDisplayMode('grid')} className={`${viewModeButtonBase} ${displayMode === 'grid' ? viewModeButtonActive : viewModeButtonInactive}`}>Grid</button>
                  <button onClick={() => setDisplayMode('list')} className={`${viewModeButtonBase} ${displayMode === 'list' ? viewModeButtonActive : viewModeButtonInactive}`}>List</button>
                  <button onClick={() => setDisplayMode('compare')} className={`${viewModeButtonBase} ${displayMode === 'compare' ? viewModeButtonActive : viewModeButtonInactive}`}>Compare</button>
                </div>
              </div>
            )}

            {!isFilterPanelOpen && allTanks.length > 0 && (
              <TankList 
                tanks={filteredTanks} 
                selectedType={selectedType}
                displayMode={displayMode}
                onTankSelect={handleOpenModal}
                onAddToCart={handleAddToCart}
              />
            )}
            {!isFilterPanelOpen && allTanks.length === 0 && !isLoading && (
                <p className="tw-text-gray-600 tw-text-center tw-py-10">No tanks available for this type.</p>
             )}
          </div>
        )}
      </main>
      <TankDetailModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        tank={selectedTankForModal} 
        onNext={handleShowNextTankInModal} 
        onPrevious={handleShowPreviousTankInModal} 
        hasNext={currentIndexInFilteredTanks < filteredTanks.length - 1}
        hasPrevious={currentIndexInFilteredTanks > 0}
        selectedType={selectedType}
        onAddToCart={handleAddToCart}
      />
      <AddToCartConfirmationModal
        isOpen={isCartConfirmationModalOpen}
        onClose={handleCloseCartConfirmationModal}
        onCheckout={handleCheckout}
        onViewCart={handleViewCart}
        cartItemCount={cartDetails.itemCount}
        cartTotalPrice={cartDetails.totalPrice}
        selectedType={selectedType}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
}

export default App;
